import { parseAlgebra } from "@/lib/tool-kit/algebra/parse";
import { exponentsOf, type Poly } from "@/lib/tool-kit/algebra/poly";
import { gcdBig } from "@/lib/tool-kit/math/bigint";
import {
  addR,
  divR,
  equalsR,
  formatPolynomial,
  intR,
  isZeroR,
  mulR,
  type NumberStyle,
  negR,
  RATIONAL_ZERO,
  type Rational,
  signR,
  subR,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

/**
 * Complex numbers with exact rational parts: a + bi with a and b fractions.
 *
 * Sums, products, quotients and integer powers stay exact. Modulus, argument and roots
 * need square roots and trigonometry, so those are floating point — except that the
 * argument of a number on an axis or a diagonal is exactly a multiple of π/4, and is
 * reported as one.
 */
export interface Complex {
  re: Rational;
  im: Rational;
}

export const COMPLEX_ZERO: Complex = { re: RATIONAL_ZERO, im: RATIONAL_ZERO };

export const cAdd = (a: Complex, b: Complex): Complex => ({
  re: addR(a.re, b.re),
  im: addR(a.im, b.im),
});

export const cSub = (a: Complex, b: Complex): Complex => ({
  re: subR(a.re, b.re),
  im: subR(a.im, b.im),
});

export const cMul = (a: Complex, b: Complex): Complex => ({
  re: subR(mulR(a.re, b.re), mulR(a.im, b.im)),
  im: addR(mulR(a.re, b.im), mulR(a.im, b.re)),
});

export const cConj = (a: Complex): Complex => ({ re: a.re, im: negR(a.im) });

export const cIsZero = (a: Complex): boolean => isZeroR(a.re) && isZeroR(a.im);

/** |z|² = a² + b². */
export function normSquared(a: Complex): Rational {
  return addR(mulR(a.re, a.re), mulR(a.im, a.im));
}

/** a ÷ b, exact; null when b is zero. */
export function cDiv(a: Complex, b: Complex): Complex | null {
  if (cIsZero(b)) {
    return null;
  }
  const denominator = normSquared(b);
  const numerator = cMul(a, cConj(b));
  return {
    re: divR(numerator.re, denominator),
    im: divR(numerator.im, denominator),
  };
}

/** z^n for an integer n; null for 0⁻ⁿ. */
export function cPow(z: Complex, n: number): Complex | null {
  if (n < 0) {
    const inverse = cDiv({ re: intR(1), im: RATIONAL_ZERO }, z);
    return inverse === null ? null : cPow(inverse, -n);
  }
  let result: Complex = { re: intR(1), im: RATIONAL_ZERO };
  let base = z;
  let exponent = n;
  while (exponent > 0) {
    if (exponent % 2 === 1) {
      result = cMul(result, base);
    }
    base = cMul(base, base);
    exponent = Math.floor(exponent / 2);
  }
  return result;
}

export type ComplexError =
  | "empty"
  | "syntax"
  | "letter"
  | "power"
  | "divide-by-variable";

export type ComplexParse =
  | { ok: true; value: Complex }
  | { ok: false; error: ComplexError; token: string };

const I_POWERS = [
  { re: 1, im: 0 },
  { re: 0, im: 1 },
  { re: -1, im: 0 },
  { re: 0, im: -1 },
];

function reduce(poly: Poly): Complex {
  let re = RATIONAL_ZERO;
  let im = RATIONAL_ZERO;
  for (const [key, coefficient] of Object.entries(poly)) {
    const power = exponentsOf(key).i ?? 0;
    const unit = I_POWERS[power % 4];
    re = addR(re, mulR(coefficient, intR(unit.re)));
    im = addR(im, mulR(coefficient, intR(unit.im)));
  }
  return { re, im };
}

const J_ONLY = /^[^iI]*$/;

/**
 * Read `3 + 4i`, `−2i`, `1/2 − (3/4)i`, `(1 + i)^2`, `j` for engineers. Products of
 * brackets are multiplied out. Division is allowed only by a plain number here.
 */
export function parseComplex(input: string): ComplexParse {
  const text = input.trim();
  if (text === "") {
    return { ok: false, error: "empty", token: "" };
  }
  const unit = J_ONLY.test(text) ? text.replace(/j/g, "i") : text;
  const parsed = parseAlgebra(unit);
  if (!parsed.ok) {
    const code = parsed.error.code;
    if (code === "divide-by-variable") {
      return { ok: false, error: "divide-by-variable", token: "" };
    }
    return { ok: false, error: "syntax", token: parsed.error.token };
  }
  const letters = new Set<string>();
  for (const key of Object.keys(parsed.poly)) {
    for (const name of Object.keys(exponentsOf(key))) {
      letters.add(name);
    }
  }
  const other = [...letters].find((name) => name !== "i");
  if (other) {
    return { ok: false, error: "letter", token: other };
  }
  return { ok: true, value: reduce(parsed.poly) };
}

/** `3 + 4i`, `−i`, `5`, `1/2 + (3/4)i` — real part first. */
export function formatComplex(
  z: Complex,
  style: NumberStyle = "fraction",
  mark = "."
): string {
  return formatPolynomial(
    [
      { coefficient: z.re, variable: "" },
      { coefficient: z.im, variable: "i" },
    ],
    style,
    mark
  );
}

// ---------------------------------------------------------------------------------------
// Polar form
// ---------------------------------------------------------------------------------------

export interface Quarter {
  /** Numerator p of the exact angle p·π/q. */
  p: number;
  q: number;
}

/** Quadrant label: 1–4, or an axis. */
export type Position =
  | "origin"
  | "positive-real"
  | "negative-real"
  | "positive-imaginary"
  | "negative-imaginary"
  | "quadrant-1"
  | "quadrant-2"
  | "quadrant-3"
  | "quadrant-4";

export function positionOf(z: Complex): Position {
  const sa = signR(z.re);
  const sb = signR(z.im);
  if (sa === 0 && sb === 0) {
    return "origin";
  }
  if (sb === 0) {
    return sa > 0 ? "positive-real" : "negative-real";
  }
  if (sa === 0) {
    return sb > 0 ? "positive-imaginary" : "negative-imaginary";
  }
  if (sa > 0) {
    return sb > 0 ? "quadrant-1" : "quadrant-4";
  }
  return sb > 0 ? "quadrant-2" : "quadrant-3";
}

/** The exact argument p·π/4 when z lies on an axis or a diagonal, else null. */
export function exactArgument(z: Complex): Quarter | null {
  const sa = signR(z.re);
  const sb = signR(z.im);
  if (sa === 0 && sb === 0) {
    return null;
  }
  if (sb === 0) {
    return { p: sa > 0 ? 0 : 4, q: 4 };
  }
  if (sa === 0) {
    return { p: sb > 0 ? 2 : -2, q: 4 };
  }
  if (equalsR(absValue(z.re), absValue(z.im))) {
    if (sa > 0) {
      return { p: sb > 0 ? 1 : -1, q: 4 };
    }
    return { p: sb > 0 ? 3 : -3, q: 4 };
  }
  return null;
}

function absValue(a: Rational): Rational {
  return signR(a) < 0 ? negR(a) : a;
}

/** "π/4", "−3π/4", "π", "0", "2π/3" — an angle p·π/q in lowest terms. */
export function formatPiFraction(p: number, q: number): string {
  if (p === 0) {
    return "0";
  }
  const divisor = Number(gcdBig(BigInt(Math.abs(p)), BigInt(q)));
  const num = p / divisor;
  const den = q / divisor;
  const sign = num < 0 ? "−" : "";
  const size = Math.abs(num);
  const top = size === 1 ? "π" : `${size}π`;
  return den === 1 ? `${sign}${top}` : `${sign}${top}/${den}`;
}

export interface Polar {
  /** Modulus as a float; the exact value is √(normSquared). */
  modulus: number;
  /** Principal argument in radians, (−π, π]. */
  argument: number;
  exact: Quarter | null;
}

/** Modulus and principal argument; null for zero, which has no argument. */
export function toPolar(z: Complex): Polar | null {
  if (cIsZero(z)) {
    return null;
  }
  const a = toNumberR(z.re);
  const b = toNumberR(z.im);
  const exact = exactArgument(z);
  const argument = exact ? (exact.p * Math.PI) / exact.q : Math.atan2(b, a);
  return { modulus: Math.hypot(a, b), argument, exact };
}

export const toDegrees = (radians: number): number => (radians * 180) / Math.PI;

/** The nth roots of z, k = 0 … n−1, as floats with their angles. */
export interface RootEntry {
  index: number;
  re: number;
  im: number;
  /** Angle in radians. */
  angle: number;
  /** Exact angle p·π/q when the argument of z is a multiple of π/4. */
  exact: Quarter | null;
}

export function nthRoots(z: Complex, n: number): RootEntry[] | null {
  const polar = toPolar(z);
  if (polar === null || n < 1) {
    return null;
  }
  const radius = polar.modulus ** (1 / n);
  const roots: RootEntry[] = [];
  for (let k = 0; k < n; k += 1) {
    const angle = (polar.argument + 2 * Math.PI * k) / n;
    const exact = polar.exact
      ? { p: polar.exact.p + 8 * k * (polar.exact.q / 4), q: polar.exact.q * n }
      : null;
    roots.push({
      index: k,
      re: radius * Math.cos(angle),
      im: radius * Math.sin(angle),
      angle,
      exact,
    });
  }
  return roots;
}
